import api from './api';

export const warehouseService = {
  // Get warehouses
  getWarehouses: async (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
    const response = await api.get('/warehouses', { params });
    return response.data;
  },

  // Create warehouse
  createWarehouse: async (payload: { name: string; code: string; address: string; totalSpaceSqft: number; status?: string }) => {
    const response = await api.post('/warehouses', payload);
    return response.data;
  },

  // Update warehouse
  updateWarehouse: async (warehouseId: number, payload: {
    name?: string;
    code?: string;
    address?: string;
    totalSpaceSqft?: number;
    status?: string;
    companyName?: string;
    companyGstin?: string;
    companyPan?: string;
    bankName?: string;
    bankAccountNo?: string;
    bankIfsc?: string;
    bankBranch?: string;
    companyTagline?: string;
    companyEmail?: string;
    companyJurisdiction?: string;
  }) => {
    const response = await api.patch(`/warehouses/${warehouseId}`, payload);
    return response.data;
  },

  // Get zones by warehouse
  getZones: async (warehouseId: number, params?: { page?: number; limit?: number; status?: string }) => {
    const response = await api.get(`/warehouses/${warehouseId}/zones`, { params });
    return response.data;
  },

  // Get warehouse space utilization (total vs. area assigned to zones)
  getWarehouseUtilization: async (warehouseId: number) => {
    const response = await api.get(`/warehouses/${warehouseId}/zone-utilization`);
    return response.data;
  },

  // Create zone
  createZone: async (payload: { warehouseId: number; code: string; name?: string; zoneType: string; status?: string; areaSqft?: number; locationSizeSqft?: number }) => {
    const response = await api.post('/zones', payload);
    return response.data;
  },

  // Update zone
  updateZone: async (zoneId: number, payload: { code?: string; name?: string; zoneType?: string; status?: string; areaSqft?: number; locationSizeSqft?: number | null }) => {
    const response = await api.patch(`/zones/${zoneId}`, payload);
    return response.data;
  },

  // Get locations by zone
  getZoneLocations: async (zoneId: number, params?: { page?: number; limit?: number }) => {
    const response = await api.get(`/zones/${zoneId}/locations`, { params });
    return response.data;
  },

  // Generate locations for zone
  generateZoneLocations: async (zoneId: number, payload: { locations: number }) => {
    const response = await api.post(`/zones/${zoneId}/generate-locations`, payload);
    return response.data;
  },

  // Get racks by warehouse
  getRacks: async (warehouseId: number, params?: { page?: number; limit?: number; status?: string }) => {
    const response = await api.get(`/warehouses/${warehouseId}/racks`, { params });
    return response.data;
  },

  // Create rack
  createRack: async (payload: { warehouseId: number; code: string; name?: string; rackType?: string; status?: string; locationSizeSqft?: number | null; rackCount?: number }) => {
    const response = await api.post('/racks', payload);
    return response.data;
  },

  // Update rack
  updateRack: async (rackId: number, payload: { code?: string; name?: string; rackType?: string; status?: string; locationSizeSqft?: number | null; rackCount?: number | null }) => {
    const response = await api.patch(`/racks/${rackId}`, payload);
    return response.data;
  },

  // Get locations by rack
  getLocations: async (rackId: number, params?: { page?: number; limit?: number }) => {
    const response = await api.get(`/racks/${rackId}/locations`, { params });
    return response.data;
  },

  // Generate locations for rack
  generateLocations: async (rackId: number, payload: { locations: number }) => {
    const response = await api.post(`/racks/${rackId}/generate-locations`, payload);
    return response.data;
  },

  // Get rack locations with occupancy details
  getRackLocationsWithOccupancy: async (rackId: number) => {
    const response = await api.get(`/warehouse-structure/racks/${rackId}/locations`);
    return response.data;
  },

  // Get zone locations with occupancy details
  getZoneLocationsWithOccupancy: async (zoneId: number) => {
    const response = await api.get(`/warehouse-structure/zones/${zoneId}/locations`);
    return response.data;
  },

  // Get zone occupancy
  getZoneOccupancy: async (zoneId: number) => {
    const response = await api.get(`/warehouse-structure/zones/${zoneId}/occupancy`);
    return response.data;
  },

  // Get all zones occupancy for a warehouse
  getAllZonesOccupancy: async (warehouseId: number) => {
    const response = await api.get(`/warehouse-structure/warehouses/${warehouseId}/zones-occupancy`);
    return response.data;
  },

  // Get all racks summary for a warehouse
  getAllRacksSummary: async (warehouseId: number) => {
    const response = await api.get(`/warehouse-structure/warehouses/${warehouseId}/racks-summary`);
    return response.data;
  },

  // Get full warehouse structure (zones + racks + locations)
  getWarehouseStructure: async (warehouseId: number) => {
    const response = await api.get(`/warehouse-structure/warehouses/${warehouseId}/structure`);
    return response.data;
  },

  // Get all pallets in a warehouse with location details
  getWarehousePallets: async (warehouseId: number) => {
    const response = await api.get(`/warehouse-structure/warehouses/${warehouseId}/pallets`);
    return response.data;
  },

  // Relocate a pallet to a different zone or rack location
  relocatePallet: async (
    storageId: number,
    payload: { storageType: 'ground' | 'rack'; zoneId?: number; zoneLocationId?: number; rackId?: number; locationId?: number },
  ) => {
    const response = await api.patch(`/warehouse-structure/pallets/${storageId}/relocate`, payload);
    return response.data;
  },

  // Merge multiple storage records into one
  mergePallets: async (payload: { targetStorageId: number; sourceStorageIds: number[] }) => {
    const response = await api.post('/warehouse-structure/pallets/merge', payload);
    return response.data;
  },
};
